import React, { useState, useEffect } from "react";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import axios from "axios";
import API_BASE_URL from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { canAccess } from "../../utils/permissions";
import axiosInstance from "../../utils/axiosInstance";

const Users = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const { user: currentUser } = useAuth();
  const [modalMode, setModalMode] = useState("add");
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [role, setRole] = useState("");

  // ✅ diagnostic centers + selection (replaces centers)
  const [diagnosticCenters, setDiagnosticCenters] = useState([]);
  const [selectedDiagnosticCenterIds, setSelectedDiagnosticCenterIds] = useState([]);
  const [diagnosticCentersTouched, setDiagnosticCentersTouched] = useState(false);

  // Form states
  // Populated after ROLE_PRESETS is defined — placeholder here, reset in useEffect
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", city: "",
    role: "admin", status: "active", password: "",
    rights: {
      user: false, enquiry: false, master: false, patient: false, vendor: false,
      orders: false, onlineOrders: false, notification: false, reports: false,
      prescriptions: false, payments: false, coupons: false, centers: false,
      banners: false, cart: false, dashboard: false, labOrders: false,
      labReport: false, packages: false, statistics: false,
      canCreate: false, canEdit: false, canDelete: false, canDownload: false,
      canCancel: false, canReschedule: false, canDispatch: false,
    },
  });

  const roles = [
    { value: "superadmin",      label: "Super Admin",                    color: "purple" },
    { value: "admin",           label: "Admin (Diagnostic Venue Level)", color: "orange" },
    { value: "receptionist",    label: "Receptionist",                   color: "teal"   },
    { value: "lab_technician",  label: "Lab Technician",                 color: "blue"   },
    { value: "quality_officer", label: "Quality Officer",                color: "green"  },
  ];

  const ROLE_DESCRIPTIONS = {
    superadmin:      "Full system access. Manages master data, all centers, users, reports & audit logs.",
    admin:           "Complete operations for a specific diagnostic center. Billing, payments, staff monitoring. No master configuration.",
    receptionist:    "Front desk: walk-in registration, billing, payment entry, day closing, generate report links. No result entry.",
    lab_technician:  "Lab processing and result entry. View assigned orders, workspace queue, generate report links. No billing.",
    quality_officer: "Verifies & approves results, final report dispatch. No billing, no booking edits, no admin settings.",
  };

  // ── Preset rights per role (from PDF spec) ──────────────────────────────────
  const ROLE_PRESETS = {
    superadmin: {
      dashboard: true, orders: true, onlineOrders: true, labOrders: true,
      labReport: true, payments: true, patient: true, enquiry: true,
      cart: true, prescriptions: true, master: true, vendor: true,
      packages: true, user: true, banners: true, centers: true,
      coupons: true, notification: true, reports: true, statistics: true,
      canCreate: true, canEdit: true, canDelete: true, canDownload: true,
      canCancel: true, canReschedule: true, canDispatch: true,
    },
    admin: {
      // Full operations for their center; no master data
      dashboard: true, orders: true, onlineOrders: true, labOrders: true,
      labReport: true, payments: true, patient: true, enquiry: true,
      cart: true, prescriptions: true, master: false, vendor: true,
      packages: true, user: false, banners: false, centers: true,
      coupons: true, notification: true, reports: true, statistics: true,
      canCreate: true, canEdit: true, canDelete: false, canDownload: true,
      canCancel: true, canReschedule: true, canDispatch: true,
    },
    receptionist: {
      // Walk-in registration, billing, day closing, limited view
      dashboard: true, orders: true, onlineOrders: false, labOrders: false,
      labReport: false, payments: true, patient: true, enquiry: true,
      cart: false, prescriptions: false, master: false, vendor: false,
      packages: false, user: false, banners: false, centers: false,
      coupons: false, notification: false, reports: false, statistics: false,
      canCreate: true, canEdit: false, canDelete: false, canDownload: true,
      canCancel: false, canReschedule: false, canDispatch: false,
    },
    lab_technician: {
      // Lab processing, result entry, generate report links; no billing
      dashboard: true, orders: true, onlineOrders: false, labOrders: true,
      labReport: true, payments: false, patient: false, enquiry: false,
      cart: false, prescriptions: false, master: false, vendor: false,
      packages: false, user: false, banners: false, centers: false,
      coupons: false, notification: false, reports: false, statistics: false,
      canCreate: false, canEdit: true, canDelete: false, canDownload: true,
      canCancel: false, canReschedule: false, canDispatch: false,
    },
    quality_officer: {
      // View all results, approve, dispatch; no billing, no booking edits
      dashboard: true, orders: false, onlineOrders: false, labOrders: true,
      labReport: true, payments: false, patient: false, enquiry: false,
      cart: false, prescriptions: false, master: false, vendor: false,
      packages: false, user: false, banners: false, centers: false,
      coupons: false, notification: false, reports: false, statistics: false,
      canCreate: false, canEdit: false, canDelete: false, canDownload: true,
      canCancel: false, canReschedule: false, canDispatch: true,
    },
  };

  const applyRolePreset = (roleValue) => {
    const preset = ROLE_PRESETS[roleValue];
    if (!preset) return;
    setFormData((prev) => ({ ...prev, role: roleValue, rights: { ...preset } }));
  };

  const moduleLabels = {
    dashboard: "Operations Hub",
    orders: "Workspace",
    onlineOrders: "Online Booking",
    labOrders: "Lab Orders",
    labReport: "Lab Report",
    payments: "Payments",
    patient: "Reg Patients",
    enquiry: "Enquiry Management",
    cart: "Cart Activity",
    prescriptions: "Prescriptions",
    master: "Master Data",
    vendor: "Vendor Management",
    packages: "Report Master",
    user: "User Management",
    banners: "Banners Management",
    centers: "B2B Centers",
    coupons: "Coupons",
    notification: "Notifications",
    reports: "Reports & Analytics",
    statistics: "Statistics",
  };

  // ✅ Fetch diagnostic centers list
  const fetchDiagnosticCenters = async () => {
    try {
      const res = await axiosInstance.get(`${API_BASE_URL}/api/diagnostic-centers`, {
        params: { limit: 1000 },
      });

      const list = res.data?.diagnosticCenters || res.data?.data || res.data || [];
      setDiagnosticCenters(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Error fetching diagnostic centers:", err);
      setDiagnosticCenters([]);
    }
  };

  // ✅ Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API_BASE_URL}/api/users`, {
        params: { search, status, role },
      });

      setUsers(response.data.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [search, status, role]);

  useEffect(() => {
    fetchDiagnosticCenters();
    // seed formData with admin preset on mount
    setFormData((prev) => ({ ...prev, rights: { ...ROLE_PRESETS.admin } }));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "role") {
      applyRolePreset(value);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleModuleRightsChange = (module) => {
    setFormData((prev) => ({
      ...prev,
      rights: { ...prev.rights, [module]: !prev.rights[module] },
    }));
  };

  const handleGlobalRightsChange = (right) => {
    setFormData((prev) => ({
      ...prev,
      rights: { ...prev.rights, [right]: !prev.rights[right] },
    }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      city: "",
      role: "admin",
      status: "active",
      password: "",
      rights: { ...ROLE_PRESETS.admin },
    });

    // ✅ reset diagnostic center selection
    setSelectedDiagnosticCenterIds([]);
    setDiagnosticCentersTouched(false);
  };

  const handleAddUser = () => {
    setModalMode("add");
    setSelectedUser(null);
    resetForm();
    setShowUserModal(true);
  };

  const handleEdit = (user) => {
    setModalMode("edit");
    setSelectedUser(user);

    const userRights = user.rights || {};
    // Map legacy role values to new names
    const mappedRole =
      user.role === "operator" ? "receptionist" :
      user.role === "viewer"   ? "lab_technician" :
      user.role;
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone || "",
      city: user.city || "",
      role: mappedRole,
      status: user.status,
      password: "",
      rights: {
        user: userRights.user || false,
        dashboard: userRights.dashboard || false,
        master: userRights.master || false,
        patient: userRights.patient !== undefined ? userRights.patient : false,
        vendor: userRights.vendor || false,
        orders: userRights.orders !== undefined ? userRights.orders : false,
        onlineOrders: userRights.onlineOrders || false,
        banners: userRights.banners !== undefined ? userRights.banners : false,
        enquiry: userRights.enquiry !== undefined ? userRights.enquiry : false,
        notification:
          userRights.notification !== undefined ? userRights.notification : false,
        reports: userRights.reports !== undefined ? userRights.reports : false,
        cart: userRights.cart !== undefined ? userRights.cart : false,
        centers: userRights.centers !== undefined ? userRights.centers : false,
        prescriptions:
          userRights.prescriptions !== undefined ? userRights.prescriptions : false,
        payments: userRights.payments !== undefined ? userRights.payments : false,
        coupons: userRights.coupons !== undefined ? userRights.coupons : false,
        labOrders: userRights.labOrders || false,
        labReport: userRights.labReport || false,
        packages: userRights.packages || false,
        statistics: userRights.statistics || false,
        canCreate: userRights.canCreate !== undefined ? userRights.canCreate : false,
        canEdit: userRights.canEdit !== undefined ? userRights.canEdit : false,
        canDelete: userRights.canDelete || false,
        canDownload: userRights.canDownload || false,
        canCancel: userRights.canCancel || false,
        canReschedule: userRights.canReschedule || false,
        canDispatch: userRights.canDispatch || false,
      },
    });

    // ✅ Prefill diagnostic centers (backend must include assignedDiagnosticCenters)
    const assigned = (user.assignedDiagnosticCenters || [])
      .map((x) => x.diagnosticCenterId ?? x.diagnosticCenter?.id)
      .filter(Boolean);

    setSelectedDiagnosticCenterIds(assigned);
    setDiagnosticCentersTouched(false);

    setShowUserModal(true);
  };

  const handleSubmitUser = async (e) => {
    e.preventDefault();
    try {
      if (modalMode === "add" && !formData.password) {
        alert("Please enter a password for new user");
        return;
      }

      if (modalMode === "add") {
        const payload = { ...formData };

        // ✅ optional: only send if selected
        if (selectedDiagnosticCenterIds.length > 0) {
          payload.diagnosticCenterIds = selectedDiagnosticCenterIds;
        }

        await axios.post(`${API_BASE_URL}/api/users/create`, payload);
      } else {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;

        // ✅ send diagnosticCenterIds only if user touched selection
        if (diagnosticCentersTouched) {
          updateData.diagnosticCenterIds = selectedDiagnosticCenterIds; // can be [] to clear
        }

        await axios.put(`${API_BASE_URL}/api/users/${selectedUser.id}`, updateData);
      }

      setShowUserModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error(`Error ${modalMode === "add" ? "adding" : "updating"} user:`, error);
      alert(`Failed to ${modalMode === "add" ? "add" : "update"} user`);
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await axios.delete(`${API_BASE_URL}/api/users/${userId}`);
        fetchUsers();
      } catch (error) {
        console.error("Error deleting user:", error);
        alert("Failed to delete user");
      }
    }
  };

  const handleMenuToggle = () => setSidebarOpen(!sidebarOpen);
  const handleSidebarClose = () => setSidebarOpen(false);

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return "bg-green-50 text-green-700 border border-green-200";
      case "inactive":
        return "bg-gray-50 text-gray-700 border border-gray-200";
      case "suspended":
        return "bg-red-50 text-red-700 border border-red-200";
      default:
        return "bg-gray-50 text-gray-700 border border-gray-200";
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case "superadmin":       return "bg-purple-50 text-purple-700 border border-purple-200";
      case "admin":            return "bg-orange-50 text-orange-700 border border-orange-200";
      case "receptionist":     return "bg-teal-50 text-teal-700 border border-teal-200";
      case "lab_technician":   return "bg-blue-50 text-blue-700 border border-blue-200";
      case "quality_officer":  return "bg-green-50 text-green-700 border border-green-200";
      // legacy labels kept for backwards-compat display
      case "operator":         return "bg-teal-50 text-teal-700 border border-teal-200";
      case "viewer":           return "bg-gray-50 text-gray-700 border border-gray-200";
      default:                 return "bg-gray-50 text-gray-700 border border-gray-200";
    }
  };

  const hasModulePermission = (rights, module) => rights && rights[module] === true;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main
        className={`pt-16 transition-all duration-300 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-4">
          <Breadcrumb />

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900 mb-1">
                Users Management
              </h1>
              <p className="text-xs text-gray-600">
                Manage user accounts, roles, and permissions
              </p>
            </div>
            <Button
              onClick={handleAddUser}
              disabled={!canAccess(currentUser, "canCreate")}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-2"
            >
              <Icon name="Plus" size={14} className="mr-1" />
              Add User
            </Button>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm mb-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Search Users
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                    <Icon name="Search" size={14} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md bg-white placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs"
                  />
                </div>
              </div>

              <div className="sm:w-32">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full px-2 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs"
                >
                  <option value="">All Roles</option>
                  {roles.map((roleOption) => (
                    <option key={roleOption.value} value={roleOption.value}>
                      {roleOption.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    User Accounts
                  </h3>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {users.length} users found
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-12">
                      SL No
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      User Info
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Permissions
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-6 text-center">
                        <div className="flex justify-center items-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">Loading users...</p>
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-6 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <Icon name="Users" size={32} className="text-gray-400 mb-2" />
                          <p className="text-gray-500 font-medium text-xs">No users found</p>
                          <p className="text-gray-400 text-xs mt-0.5">
                            {search || status || role
                              ? "Try adjusting your search or filters"
                              : "No users have been added yet"}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    users.map((user, index) => (
                      <tr
                        key={user.id}
                        className="hover:bg-gray-50 transition-colors duration-150"
                      >
                        <td className="px-3 py-2 text-gray-500 font-medium">
                          {index + 1}
                        </td>

                        <td className="px-3 py-2">
                          <div className="text-xs text-gray-900">
                            {new Date(user.createdAt).toLocaleDateString()}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(user.createdAt).toLocaleTimeString()}
                          </div>
                        </td>

                        <td className="px-3 py-2">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-7 w-7 bg-blue-100 rounded-md flex items-center justify-center">
                              <Icon name="User" size={14} className="text-blue-600" />
                            </div>
                            <div className="ml-2">
                              <div className="text-xs font-medium text-gray-900">
                                {user.name}
                              </div>
                              <div className="text-xs text-gray-500">{user.email}</div>

                              {/* ✅ Optional: diagnostic centers display */}
                              <div className="text-[11px] text-gray-500 mt-0.5">
                                {(user.assignedDiagnosticCenters?.length || 0) > 0
                                  ? user.assignedDiagnosticCenters
                                      .map((x) => x.diagnosticCenter?.name)
                                      .filter(Boolean)
                                      .join(", ")
                                  : "All Diagnostic Centers"}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="px-3 py-2">
                          <div className="text-xs text-gray-900">{user.phone || "N/A"}</div>
                          <div className="text-xs text-gray-500">{user.city || "N/A"}</div>
                        </td>

                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadge(
                              user.role
                            )}`}
                          >
                            {roles.find((r) => r.value === user.role)?.label ||
                              (user.role === "operator" ? "Receptionist" :
                               user.role === "viewer"   ? "Lab Technician" :
                               user.role)}
                          </span>
                        </td>

                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                              user.status
                            )}`}
                          >
                            {user.status}
                          </span>
                        </td>

                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-0.5 max-w-[150px]">
                            {Object.keys(moduleLabels).map(
                              (module) =>
                                hasModulePermission(user.rights || {}, module) && (
                                  <span
                                    key={module}
                                    className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-800 truncate"
                                    title={moduleLabels[module]}
                                  >
                                    {moduleLabels[module].split(" ")[0]}
                                  </span>
                                )
                            )}
                            {user.rights?.canCreate && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-800">
                                Create
                              </span>
                            )}
                            {user.rights?.canEdit && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800">
                                Edit
                              </span>
                            )}
                            {user.rights?.canDelete && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-800">
                                Delete
                              </span>
                            )}
                            {user.rights?.canDownload && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-purple-100 text-purple-800">
                                Download
                              </span>
                            )}
                            {user.rights?.canCancel && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-orange-100 text-orange-800">
                                Cancel
                              </span>
                            )}
                            {user.rights?.canReschedule && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-cyan-100 text-cyan-800">
                                Reschedule
                              </span>
                            )}
                            {user.rights?.canDispatch && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-emerald-100 text-emerald-800">
                                Dispatch
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="px-3 py-2">
                          <div className="flex justify-end space-x-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="Edit"
                              disabled={!canAccess(currentUser, "canEdit")}
                              className={`
                                text-blue-600 border border-blue-200 text-xs px-2 py-1
                                ${
                                  !canAccess(currentUser, "canEdit")
                                    ? "opacity-40 cursor-not-allowed hover:text-blue-600 hover:border-blue-200"
                                    : "hover:text-blue-800 hover:border-blue-300"
                                }
                              `}
                              onClick={() =>
                                canAccess(currentUser, "canEdit") && handleEdit(user)
                              }
                            />

                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="Trash2"
                              disabled={!canAccess(currentUser, "canDelete")}
                              className={`
                                text-red-600 border border-red-200 text-xs px-2 py-1
                                ${
                                  !canAccess(currentUser, "canDelete")
                                    ? "opacity-40 cursor-not-allowed hover:text-red-600 hover:border-red-200"
                                    : "hover:text-red-800 hover:border-red-300"
                                }
                              `}
                              onClick={() =>
                                canAccess(currentUser, "canDelete") && handleDelete(user.id)
                              }
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {showUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">
                  {modalMode === "add" ? "Add New User" : "Edit User"}
                </h3>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmitUser}>
              <div className="px-4 py-3 space-y-4 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {modalMode === "add" ? "Password *" : "New Password (Optional)"}
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required={modalMode === "add"}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={
                        modalMode === "edit"
                          ? "Leave blank to keep current password"
                          : "Enter password"
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {roles.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                    {ROLE_DESCRIPTIONS[formData.role] && (
                      <p className="text-[10px] text-gray-500 mt-1.5 leading-relaxed bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
                        {ROLE_DESCRIPTIONS[formData.role]}
                        <span className="block text-blue-500 mt-0.5">Permissions auto-loaded — you can still adjust below.</span>
                      </p>
                    )}
                  </div>

                  {/* ✅ DIAGNOSTIC CENTER ASSIGNMENT (Optional) */}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Assigned Diagnostic Centers (Optional)
                    </label>

                    <div className="border border-gray-300 rounded-md p-2 bg-white max-h-40 overflow-y-auto">
                      {diagnosticCenters.length === 0 ? (
                        <div className="text-xs text-gray-500">
                          No diagnostic centers found
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {diagnosticCenters.map((dc) => {
                            const checked = selectedDiagnosticCenterIds.includes(dc.id);
                            return (
                              <label
                                key={dc.id}
                                className="flex items-center gap-2 text-xs"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    setDiagnosticCentersTouched(true);
                                    setSelectedDiagnosticCenterIds((prev) => {
                                      if (e.target.checked) return [...prev, dc.id];
                                      return prev.filter((id) => id !== dc.id);
                                    });
                                  }}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-gray-800">{dc.name}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <p className="text-[11px] text-gray-500 mt-1">
                      Leave empty to allow access without diagnostic center restrictions.
                    </p>
                  </div>
                </div>

                {/* Module Permissions */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Module Permissions
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-3 bg-gray-50 rounded-lg">
                    {Object.keys(moduleLabels).map((module) => (
                      <div
                        key={module}
                        className="border border-gray-200 rounded-lg p-3 bg-white"
                      >
                        <label className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-gray-900">
                            {moduleLabels[module]}
                          </span>
                          <input
                            type="checkbox"
                            checked={formData.rights[module] || false}
                            onChange={() => handleModuleRightsChange(module)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Global Permissions */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Global Permissions
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                    {[
                      { key: "canCreate", label: "Create" },
                      { key: "canEdit", label: "Edit" },
                      { key: "canDelete", label: "Delete" },
                      { key: "canDownload", label: "Download" },
                      { key: "canCancel", label: "Cancel Order" },
                      { key: "canReschedule", label: "Reschedule Order" },
                      { key: "canDispatch", label: "Dispatch Report" },
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={Boolean(formData.rights[key])}
                          onChange={() => handleGlobalRightsChange(key)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-xs text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowUserModal(false)}
                  className="text-xs px-3 py-1.5"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="solid"
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5"
                >
                  {modalMode === "add" ? "Add User" : "Update User"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
